import * as React from 'react';
import { getMessagesUrl, getNotificationsUrl } from '@/utils/urls';
import { getUserFirstName, logOutBidder } from '@/redux/modules/user';
import styled, { useTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faHeart } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import isEqual from 'react-fast-compare';
import { isWhiteLabel } from '@/redux/modules/whiteLabel';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import MessagesNotification from '@/components/Header/Notifications/MessagesNotification';
import NotificationsContainer from '@/components/Header/Notifications/NotificationsContainer';
import PaddleIconSvg from './PaddleIconSvg';
import Popover from '@liveauctioneers/caterwaul-components/lib/Popover/Popover';
import styles from './LoggedInMenu.module.scss';
import { useLocation } from 'react-router';
import useWindowSize from '@/hooks/useWindowSize';

type Props = {
    parent: any;
};

const LoggedInMenu = ({ parent }: Props) => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const firstName = useSelector(getUserFirstName);
    const whiteLabel = useSelector(isWhiteLabel);
    const { width: windowWidth } = useWindowSize();

    // @ts-ignore
    const { breakpoints } = useTheme();
    const mobile = windowWidth <= breakpoints.tabletNarrowWidthNum;

    const logOut = React.useCallback(() => dispatch(logOutBidder()), [dispatch]);

    return (
        <div className={styles.loggedIn}>
            {!whiteLabel && <NotificationsContainer mobile={mobile} parent={parent} pathname={pathname} />}
            <StyledMenuItemWrapper>
                <Link key="saved-items" route to="/dashboard/saved-items">
                    <StyledMenuItem>
                        <StyledMenuItemFAI icon={faHeart} />
                        <StyledMenuItemText>
                            <FormattedMessage id="userMenu.favorites" />
                        </StyledMenuItemText>
                    </StyledMenuItem>
                </Link>
            </StyledMenuItemWrapper>
            {!mobile && (
                <StyledMenuItemWrapper>
                    <Link className={styles.myBidsHover} key="my-bids" route to="/dashboard/my-bids">
                        <StyledMenuItem>
                            <PaddleIconSvg className={styles.paddleIcon} />
                            <StyledMenuItemText>
                                <FormattedMessage id="userMenu.my_bids" />
                            </StyledMenuItemText>
                        </StyledMenuItem>
                    </Link>
                </StyledMenuItemWrapper>
            )}
            <Popover
                childrenClickClosesPopover
                hover={false}
                placement="bottom"
                trigger={
                    <StyledMenuItemWrapper>
                        <StyledMenuItem>
                            <StyledMenuItemFAI icon={faUser} />
                            <StyledMenuItemText>
                                {Boolean(firstName) && firstName.length < 9 ? (
                                    <FormattedMessage id="userMenu.greeting" values={{ firstName }} />
                                ) : (
                                    <FormattedMessage id="userMenu.my_account" />
                                )}
                            </StyledMenuItemText>
                        </StyledMenuItem>
                        <StyledCaretFAI icon={faCaretDown} />
                    </StyledMenuItemWrapper>
                }
            >
                <StyledContent>
                    {mobile && (
                        <StyledNavigationLink key="my-bids" route to="/dashboard/my-bids">
                            <FormattedMessage id="userMenu.my_bids" />
                        </StyledNavigationLink>
                    )}
                    <StyledNavigationLink key="my-auctions" route to="/dashboard/my-auctions">
                        <FormattedMessage id="my_auctions" />
                    </StyledNavigationLink>
                    <StyledNavigationLink key="won-items" route to="/dashboard/won-items">
                        <FormattedMessage id="won_items" />
                    </StyledNavigationLink>
                    <StyledNavigationLink key="messages" route to={getMessagesUrl()}>
                        <MessagesNotification />
                    </StyledNavigationLink>
                    <StyledNavigationLink key="profile" route to="/dashboard/profile">
                        <FormattedMessage id="account_settings" />
                    </StyledNavigationLink>
                    <StyledNavigationLink key="followed_searches" route to="/dashboard/search-and-alerts/">
                        <FormattedMessage id="followed_searches" />
                    </StyledNavigationLink>
                    <StyledNavigationLink key="followed_auctioneers" route to="/dashboard/followed-auctioneers">
                        <FormattedMessage id="followed_auctioneers" />
                    </StyledNavigationLink>
                    <StyledNavigationLink key="notifications" route to={getNotificationsUrl()}>
                        <FormattedMessage id="notificationsPage.title" />
                    </StyledNavigationLink>
                    <StyledLogoutLink key="log-out" onClick={logOut} to="">
                        <FormattedMessage id="log_out" />
                    </StyledLogoutLink>
                </StyledContent>
            </Popover>
        </div>
    );
};

const StyledMenuItemWrapper = styled.div`
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    cursor: pointer;
    border-left: 1px solid ${({ theme }) => theme.colors.grey400};
    padding: 4px 0;
    display: flex;

    @media (max-width: 768px) {
        border-left: none;
    }

    & > svg {
        color: ${({ theme }) => theme.colors.grey100};
    }

    :hover,
    :hover span {
        color: ${({ theme }) => theme.colors.blue200};

        svg {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }

    & > a {
        text-decoration: none !important;
    }
`;

const StyledMenuItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 89px;
    min-width: 89px;

    @media (max-width: 768px) {
        width: 44px;
        min-width: 44px;
    }
`;

const StyledMenuItemText = styled.span`
    line-height: 12px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.grey100};
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    padding-top: 5px;

    @media (max-width: 768px) {
        display: none;
    }
`;

const StyledMenuItemFAI = styled(FontAwesomeIcon)`
    align-self: center;
    font-size: 22px;
    color: ${({ theme }) => theme.colors.grey100};
`;

const StyledCaretFAI = styled(FontAwesomeIcon)`
    margin-left: -15px;
    margin-top: -5px;

    @media (max-width: 768px) {
        display: none;
    }
`;

const StyledContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px 0 0;
`;

const StyledNavigationLink = styled(Link)`
    && {
        /* Link and text don't play well together. OP-621 is to fix that */
        color: ${({ theme }) => theme.colors.grey100};
        cursor: pointer;
        font-family: ${({ theme }) => theme.typography.fontPrimary};
        font-size: 14px;
        letter-spacing: 0.5px;
        line-height: 43px;
        padding: 2px 20px;
        text-align: left;
        text-decoration: none;

        :hover {
            color: ${({ theme }) => theme.colors.blue200};
            background-color: ${({ theme }) => theme.colors.grey500};
        }
    }
`;

const StyledLogoutLink = styled(StyledNavigationLink)`
    &&& {
        text-transform: uppercase;
        margin-top: 14px;
        text-align: center !important;
        border-top: 1px solid ${({ theme }) => theme.colors.grey400};
        color: ${({ theme }) => theme.colors.blue100};
        font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
        padding: 16px 10px;
        border-radius: 0 0 2px 2px;

        &:hover {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;

export default React.memo<Props>(LoggedInMenu, isEqual);
